import React, { useState } from 'react';
import { Form, Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { StepComponentProps } from './OrganizationWizard';
import { CategoryOption, categoryApi } from 'areas/settings/category/api/categoryApi';
import { SurveystructureCompliance } from 'store/generated/admin/category';
import CategoryOptionModal from 'areas/settings/category/components/CategoryOptionModal';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { generateDistinctColor } from 'common/components/ColorPicker';
import { useToasts } from 'common/toast';

type NewCategory = {
    id: string;
    savedId?: number; // The real ID from the database if already saved
    name: string;
    default_visibility: string;
    default_prompt: string;
    default_question: string;
    options: CategoryOption[];
    isPrimary: boolean;
};

const OrganizationWizardStep2CreateCategories: React.FC<StepComponentProps> = ({
    data,
    onDataChange,
    onNext,
    onBack,
    onCancel,
    isFirstStep,
}) => {
    const { addToast } = useToasts();
    const [newCategories, setNewCategories] = useState<NewCategory[]>(data.newCategories || []);
    const [editingCategory, setEditingCategory] = useState<NewCategory | null>(null);
    const [csvOptionText, setCsvOptionText] = useState('');
    const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
    const [editingOption, setEditingOption] = useState<CategoryOption | null>(null);
    const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [createCategory] = categoryApi.useApiAdminV1CategoryCreateMutation();
    const [updateCategory] = categoryApi.useApiAdminV1CategoryPartialUpdateMutation();

    const handleAddCategory = () => {
        const isPrimary = newCategories.length === 0;
        const newCategory: NewCategory = {
            id: uuidv4(),
            name: '',
            default_visibility: 'public',
            default_prompt: 'never_ask',
            default_question: '',
            options: [],
            isPrimary,
        };
        setNewCategories([...newCategories, newCategory]);
        setEditingCategory(newCategory);
        setCurrentCategoryId(newCategory.id);
    };

    const handleUpdateCategory = (categoryId: string, updates: Partial<NewCategory>) => {
        setNewCategories(prev =>
            prev.map(cat => (cat.id === categoryId ? { ...cat, ...updates } : cat))
        );
        if (editingCategory?.id === categoryId) {
            setEditingCategory({ ...editingCategory, ...updates });
        }
    };

    const handleDeleteCategory = (categoryId: string) => {
        setNewCategories(prev => prev.filter(cat => cat.id !== categoryId));
        if (editingCategory?.id === categoryId) {
            setEditingCategory(null);
            setCurrentCategoryId(null);
        }
    };

    const handleAddOption = (categoryId: string) => {
        setCurrentCategoryId(categoryId);
        setEditingOption(null);
        setIsOptionModalOpen(true);
    };

    const handleEditOption = (categoryId: string, option: CategoryOption) => {
        setCurrentCategoryId(categoryId);
        setEditingOption(option);
        setIsOptionModalOpen(true);
    };

    const handleSaveOption = (option: CategoryOption) => {
        if (!currentCategoryId) return;

        setNewCategories(prev =>
            prev.map(cat => {
                if (cat.id !== currentCategoryId) return cat;
                const existingOptions = cat.options.filter(o => o.id !== option.id);
                return { ...cat, options: [...existingOptions, option] };
            })
        );

        if (editingCategory?.id === currentCategoryId) {
            const existingOptions = editingCategory.options.filter(o => o.id !== option.id);
            setEditingCategory({ ...editingCategory, options: [...existingOptions, option] });
        }
    };

    const handleAddOptionsFromCsv = () => {
        if (!currentCategoryId || !csvOptionText.trim()) return;

        const category = newCategories.find(cat => cat.id === currentCategoryId);
        if (!category) return;

        const rawValues = csvOptionText
            .split(/\r?\n|,|;/)
            .map(value => value.trim())
            .filter(Boolean);

        const uniqueValues = Array.from(new Set(rawValues));
        if (!uniqueValues.length) return;

        const existingOptionNames = new Set(category.options.map(option => option.name.trim().toLowerCase()));
        const newNames = uniqueValues.filter(name => !existingOptionNames.has(name.toLowerCase()));
        if (!newNames.length) return;

        const colorPool = [...category.options.map(option => option.color)];
        const newOptions: CategoryOption[] = newNames.map((name) => {
            const color = generateDistinctColor(colorPool);
            colorPool.push(color);
            return { id: uuidv4(), name, color, description: '', fr: '' };
        });

        setNewCategories(prev =>
            prev.map(cat =>
                cat.id === currentCategoryId
                    ? { ...cat, options: [...cat.options, ...newOptions] }
                    : cat
            )
        );

        if (editingCategory?.id === currentCategoryId) {
            setEditingCategory({ ...editingCategory, options: [...editingCategory.options, ...newOptions] });
        }

        setCsvOptionText('');
    };

    const handleDeleteOption = (categoryId: string, optionId: string) => {
        setNewCategories(prev =>
            prev.map(cat => {
                if (cat.id !== categoryId) return cat;
                return { ...cat, options: cat.options.filter(o => o.id !== optionId) };
            })
        );

        if (editingCategory?.id === categoryId) {
            setEditingCategory({
                ...editingCategory,
                options: editingCategory.options.filter(o => o.id !== optionId),
            });
        }
    };

    const handleNext = async () => {
        if (!data.organizationId) {
            addToast('Organization must be created before adding categories', { appearance: 'error' });
            return;
        }

        setIsSaving(true);
        try {
            // Count how many are new vs existing before mutating
            const originallyNewCount = newCategories.filter(c => !c.savedId).length;

            // Save all categories to the API - create new ones or update existing
            const updatedCategories: NewCategory[] = [];

            for (const category of newCategories) {
                const categoryData: Partial<SurveystructureCompliance> = {
                    name: category.name,
                    status: 1,
                    priority: (category.isPrimary ? 1 : 2) as 1 | 2,
                    organization: data.organizationId,
                    default_prompt: category.default_prompt,
                    default_visibility: category.default_visibility,
                    default_question: category.default_question,
                    categories: category.options,
                };

                if (category.savedId) {
                    // Update existing category
                    const result = await updateCategory({
                        id: category.savedId,
                        patchedSurveystructureCompliance: categoryData,
                    }).unwrap();

                    updatedCategories.push({ ...category, savedId: result.id });
                } else {
                    // Create new category
                    const result = await createCategory({
                        surveystructureCompliance: categoryData,
                    }).unwrap();

                    updatedCategories.push({ ...category, savedId: result.id });
                }
            }

            const createdCount = originallyNewCount;
            const updatedCount = updatedCategories.length - createdCount;

            if (createdCount > 0 && updatedCount > 0) {
                addToast(`${createdCount} created, ${updatedCount} updated`, { appearance: 'success' });
            } else if (createdCount > 0) {
                addToast(`${createdCount} ${createdCount === 1 ? 'category' : 'categories'} created`, { appearance: 'success' });
            } else if (updatedCount > 0) {
                addToast(`${updatedCount} ${updatedCount === 1 ? 'category' : 'categories'} updated`, { appearance: 'success' });
            }

            // also expose savedCategoryIds for backward compatibility
            const savedCategoryIds = updatedCategories.map(c => c.savedId!).filter(Boolean) as number[];

            // Save updated categories to wizard data and move to next step
            onDataChange({ newCategories: updatedCategories, savedCategoryIds });
            onNext();
        } catch (err) {
            console.error('Failed to save categories:', err);
            addToast('Failed to save categories', { appearance: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSkip = () => {
        onDataChange({ newCategories: [] });
        onNext();
    };

    const visibilityOptions = [
        { key: 'private', text: 'Private', value: 'private' },
        { key: 'public', text: 'Public', value: 'public' },
    ];

    const promptOptions = [
        { key: 'never_ask', text: 'Never ask', value: 'never_ask' },
        { key: 'always_ask', text: 'Always ask', value: 'always_ask' },
        { key: 'ask_if_not_found', text: 'Ask if not found', value: 'ask_if_not_found' },
    ];

    const getCurrentCategoryOptions = () => {
        if (!currentCategoryId) return [];
        const category = newCategories.find(cat => cat.id === currentCategoryId);
        return category?.options || [];
    };

    return (
        <WizardStepContainer>
            <SectionTitle>Create Categories</SectionTitle>
            <HelpText>
                Create demographic categories for this organization. The first category is the primary category (required). Additional categories are custom categories (optional).
            </HelpText>

            <ContentSection>
                <LeftPanel>
                    <PanelHeader>
                        <PanelTitle>Categories ({newCategories.length})</PanelTitle>
                        <AddButton onClick={handleAddCategory} type="button">
                            <Icon name="plus" /> Add Category
                        </AddButton>
                    </PanelHeader>

                    {newCategories.length === 0 ? (
                        <EmptyState>
                            <Icon name="folder open outline" size="huge" />
                            <EmptyText>No categories created yet</EmptyText>
                            <EmptySubtext>Click "Add Category" to create a new category</EmptySubtext>
                        </EmptyState>
                    ) : (
                        <CategoryList>
                            {newCategories.map(category => (
                                <CategoryItem
                                    key={category.id}
                                    $active={editingCategory?.id === category.id}
                                    onClick={() => {
                                        setEditingCategory(category);
                                        setCurrentCategoryId(category.id);
                                    }}
                                >
                                    <CategoryName>
                                        {category.name || '(Unnamed Category)'}
                                    </CategoryName>
                                    <CategoryMeta>
                                        {category.isPrimary ? 'Primary' : 'Custom'} • {category.options.length} option{category.options.length !== 1 ? 's' : ''}
                                        {category.options.length === 0 && (
                                            <TooltipWrapper>
                                                <CategoryWarningIcon name="warning sign" />
                                                <TooltipText>This category needs at least one option</TooltipText>
                                            </TooltipWrapper>
                                        )}
                                    </CategoryMeta>
                                    {!category.isPrimary && (
                                        <DeleteButton
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleDeleteCategory(category.id);
                                            }}
                                            type="button"
                                        >
                                            <Icon name="trash" />
                                        </DeleteButton>
                                    )}
                                </CategoryItem>
                            ))}
                        </CategoryList>
                    )}
                </LeftPanel>

                <RightPanel>
                    {editingCategory ? (
                        <>
                            <PanelHeader>
                                <PanelTitle>Configure Category</PanelTitle>
                                <TypeIndicator $isPrimary={editingCategory.isPrimary}>
                                    {editingCategory.isPrimary ? 'Primary' : 'Custom'}
                                </TypeIndicator>
                            </PanelHeader>

                            <FormContent>
                                <Form>
                                    <Form.Field required>
                                        <label>Category Name</label>
                                        <Form.Input
                                            placeholder="e.g., Department, Location, Role"
                                            value={editingCategory.name}
                                            onChange={(_, { value }) =>
                                                handleUpdateCategory(editingCategory.id, { name: value })
                                            }
                                        />
                                    </Form.Field>

                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <label>Visibility</label>
                                            <Form.Select
                                                options={visibilityOptions}
                                                value={editingCategory.default_visibility}
                                                onChange={(_, { value }) =>
                                                    handleUpdateCategory(editingCategory.id, { default_visibility: value as string })
                                                }
                                            />
                                        </Form.Field>

                                        <Form.Field>
                                            <label>Prompt</label>
                                            <Form.Select
                                                options={promptOptions}
                                                value={editingCategory.default_prompt}
                                                onChange={(_, { value }) =>
                                                    handleUpdateCategory(editingCategory.id, { default_prompt: value as string })
                                                }
                                            />
                                        </Form.Field>
                                    </Form.Group>

                                    <Form.Field>
                                        <label>Prompt Question</label>
                                        <Form.Input
                                            placeholder="What is your department?"
                                            value={editingCategory.default_question}
                                            onChange={(_, { value }) =>
                                                handleUpdateCategory(editingCategory.id, { default_question: value })
                                            }
                                        />
                                    </Form.Field>

                                    <OptionsSection>
                                        <OptionsSectionHeader>
                                            <label>Options</label>
                                            <AddOptionButton onClick={() => handleAddOption(editingCategory.id)} type="button">
                                                <Icon name="plus" /> Add Option
                                            </AddOptionButton>
                                        </OptionsSectionHeader>

                                        {editingCategory.options.length === 0 ? (
                                            <EmptyOptionsState>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <Icon name="list" />
                                                    <span>No options yet. Click "Add Option" to create, or provide a CSV list below to bulk add options.</span>
                                                </div>

                                                <Form.Field style={{ marginTop: 16 }}>
                                                    <label>Add options via CSV</label>
                                                    <Form.TextArea
                                                        placeholder="Enter option names separated by commas"
                                                        value={csvOptionText}
                                                        onChange={(_, { value }) => setCsvOptionText(String(value))}
                                                        rows={3}
                                                    />
                                                </Form.Field>
                                                <Button
                                                    primary
                                                    type="button"
                                                    disabled={!csvOptionText.trim()}
                                                    onClick={handleAddOptionsFromCsv}
                                                >
                                                    Add Options from CSV
                                                </Button>
                                            </EmptyOptionsState>
                                        ) : (
                                            <Table compact>
                                                <TableHeader>
                                                    <TableRow>
                                                        <HeaderCell>Name</HeaderCell>
                                                        <HeaderCell>Color</HeaderCell>
                                                        <HeaderCell justify="center">Actions</HeaderCell>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {editingCategory.options.map(option => (
                                                        <TableRow
                                                            key={option.id}
                                                            $selectable
                                                            onClick={() => handleEditOption(editingCategory.id, option)}
                                                        >
                                                            <TableCell $firstcol>{option.name}</TableCell>
                                                            <TableCell>
                                                                <ColorSwatch style={{ backgroundColor: option.color }} />
                                                            </TableCell>
                                                            <TableCell justify="center">
                                                                <Button
                                                                    icon="trash"
                                                                    size="mini"
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        handleDeleteOption(editingCategory.id, option.id);
                                                                    }}
                                                                    type="button"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </OptionsSection>
                                </Form>
                            </FormContent>
                        </>
                    ) : (
                        <EmptyState>
                            <Icon name="pencil" size="huge" />
                            <EmptyText>No category selected</EmptyText>
                            <EmptySubtext>Select a category from the list to configure it</EmptySubtext>
                        </EmptyState>
                    )}
                </RightPanel>
            </ContentSection>

            <CategoryOptionModal
                open={isOptionModalOpen}
                initialOption={editingOption}
                existingColors={getCurrentCategoryOptions().map(o => o.color)}
                onClose={() => setIsOptionModalOpen(false)}
                onSave={handleSaveOption}
            />

            <ButtonContainer>
                <LeftButtons>
                    <Button onClick={onBack} disabled={isFirstStep}>
                        Back
                    </Button>
                    <CancelButton onClick={onCancel}>
                        Cancel Setup
                    </CancelButton>
                </LeftButtons>
                <Button
                    primary
                    onClick={handleNext}
                    disabled={
                        isSaving ||
                        newCategories.length === 0 ||
                        newCategories.some(cat => !cat.name.trim()) ||
                        newCategories.some(cat => cat.options.length === 0)
                    }
                >
                    {isSaving ? 'Saving...' : 'Next'}
                </Button>
            </ButtonContainer>
        </WizardStepContainer>
    );
};

const WizardStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-right: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #666;
  margin: -8px 0 0 0;
`;

const ContentSection = styled.div`
  display: flex;
  gap: 20px;
  min-height: 200px;
`;

const LeftPanel = styled.div`
  flex: 0 0 300px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  overflow-x: visible;
`;

const RightPanel = styled.div`
  flex: 1;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const FormContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  background: #2185d0;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #1678c2;
  }

  i.icon {
    margin: 0 !important;
  }
`;

const CategoryList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: visible;
`;

const CategoryItem = styled.div<{ $active: boolean }>`
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => (props.$active ? '#e3f2fd' : 'white')};
  position: relative;

  &:hover {
    background: ${props => (props.$active ? '#e3f2fd' : '#f5f5f5')};
  }
`;

const CategoryName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const CategoryMeta = styled.div`
  font-size: 11px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  
  &:hover > span {
    visibility: visible;
    opacity: 1;
  }
`;

const CategoryWarningIcon = styled(Icon as any)`
  color: #f2711c;
  font-size: 14px;
  cursor: help;
  margin: 0 !important;
`;

const TooltipText = styled.span`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 8px;
  background-color: #333;
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  transition: opacity 0.2s;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: #333;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: #db2828;
  cursor: pointer;
  padding: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  i.icon {
    margin: 0 !important;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
  text-align: center;
  height: 100%;

  i.icon {
    color: #ccc;
    margin-bottom: 16px;
  }
`;

const EmptyText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
`;

const EmptySubtext = styled.div`
  font-size: 13px;
  color: #999;
`;

const OptionsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const OptionsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  label {
    font-weight: 600;
    font-size: 14px;
    color: #333;
  }
`;

const AddOptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 12px;
  background: white;
  color: #2185d0;
  border: 1px solid #2185d0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #2185d0;
    color: white;
  }

  i.icon {
    margin: 0 !important;
  }
`;

const EmptyOptionsState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 16px;
  background: #fffbea;
  border: 1px solid #f5d76e;
  border-radius: 4px;
  color: #856404;
  font-size: 13px;

  i.icon {
    color: #f5d76e;
  }
`;

const ColorSwatch = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #ddd;
`;

const TypeIndicator = styled.div<{ $isPrimary: boolean }>`
  display: inline-block;
  padding: 8px 12px;
  background: ${props => props.$isPrimary ? '#e3f2fd' : '#fff3e0'};
  border: 1px solid ${props => props.$isPrimary ? '#2185d0' : '#ff9800'};
  border-radius: 4px;
  color: ${props => props.$isPrimary ? '#2185d0' : '#ff9800'};
  font-size: 13px;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }
`;

export default OrganizationWizardStep2CreateCategories;
